import { styled } from 'styled-components';
import React, { forwardRef } from 'react';
import cn from 'clsx';
import {
    CodeBlockProps,
    PolymorphicComponent,
    PolymorphicComponentProps,
    PropsFor,
    PolymorphicRef,
} from './Typography.types';

function polymorphicForwardRef<D extends React.ElementType, E extends React.ElementType = D>(
    render: (props: PolymorphicComponentProps<PropsFor<D>, E>, ref: PolymorphicRef<E>) => React.ReactElement | null
) {
    return React.forwardRef(render) as PolymorphicComponent<PropsFor<E>>;
}

export function createTypography<D extends React.ElementType>(defaultEl: D, baseClasses: string, displayName: string) {
    function Typography<E extends React.ElementType = D>(
        { as: asProp, className, ...rest }: PolymorphicComponentProps<PropsFor<D>, E>,
        ref: PolymorphicRef<E>
    ) {
        const Component: React.ElementType = asProp ?? defaultEl;
        const mergedClass = cn(baseClasses, className);

        const propsForElement = {
            ...rest,
            ...(mergedClass ? { className: mergedClass } : {}),
        };

        return <Component ref={ref} {...propsForElement} />;
    }

    const Comp = polymorphicForwardRef<D>(Typography);

    Comp.displayName = displayName;
    return Comp;
}

// ================ HEADINGS ================= //
export const H1 = createTypography('h1', 'bbui-t-h1 cds-t-h1', 'H1');
export const H2 = createTypography('h2', 'bbui-t-h2 cds-t-h2', 'H2');
export const H3 = createTypography('h3', 'bbui-t-h3 cds-t-h3', 'H3');
export const H4 = createTypography('h4', 'bbui-t-h4 cds-t-h4', 'H4');
export const H5 = createTypography('h5', 'bbui-t-h5 cds-t-h5', 'H5');
export const H6 = createTypography('h6', 'bbui-t-h6 cds-t-h6', 'H6');

// ================ BODY ================= //

// NOTE: Body class names are also used in BaseDropdown.tsx
export const Body = createTypography('p', 'bbui-t-body cds-t-body', 'Body');
export const BodyBold = createTypography('p', 'bbui-t-body-bold cds-t-body cds-t-body--b', 'BodyBold');
export const BodyLarge = createTypography('p', 'bbui-t-body-large cds-t-body-large', 'BodyLarge');
export const BodyLargeBold = createTypography(
    'p',
    'bbui-t-body-large cds-t-body-large cds-t-body-large--b',
    'BodyLargeBold'
);
export const BodyXLarge = createTypography('p', 'bbui-t-body-xlarge cds-t-body-xlarge', 'BodyXLarge');
export const BodyXLargeBold = createTypography(
    'p',
    'bbui-t-body-xlarge cds-t-body-xlarge cds-t-body-xlarge--b',
    'BodyXLarge'
);
export const BodySmall = createTypography('p', 'bbui-t-body-small cds-t-body-small', 'BodySmall');
export const BodySmallBold = createTypography(
    'p',
    'bbui-t-body-small cds-t-body-small cds-t-body-small--b',
    'BodySmallBold'
);

// ================ Links ================= //

export const Link = createTypography('a', 'bbui-link bbui-link--default cds-link', 'Link');

/**
 * @deprecated
    Use T.Link as it includes inline styles by default
 */
export const LinkInline = styled(Link)`
    text-decoration: var(--cds-font-link-default-decoration);
`;

/**
 * @deprecated
    Use T.Link which will inherit font-size
 */
export const LinkInlineLarge = styled(LinkInline)`
    font-size: var(--cds-font-body-large-fontsize);
    line-height: var(--cds-font-body-large-lineheight);
    font-weight: var(--cds-font-body-large-weight);
`;

// ================ SUPPORTING ================= //
export const GroupHeading1 = createTypography('h2', 'bbui-t-group-heading-1 cds-t-group-heading1', 'GroupHeading1');
export const GroupHeading2 = createTypography('h3', 'bbui-t-group-heading-2 cds-t-group-heading2', 'GroupHeading2');

export const Label = createTypography('label', 'bbui-t-label cds-t-label', 'Label');
// NOTE: LabelBold class names are also used in BaseDropdown.tsx
export const LabelBold = createTypography('label', 'bbui-t-label-bold cds-t-label cds-t-label--b', 'LabelBold');

export const LabelSmall = createTypography('label', 'bbui-t-label-small cds-t-label-small', 'LabelSmall');
export const LabelXSmall = createTypography('label', 'bbui-t-label-xsmall cds-t-label-xsmall', 'LabelXSmall');

export const ButtonLabel = styled.span`
    font-size: var(--cds-font-button-label-fontsize);
    line-height: var(--cds-font-button-label-lineheight);
    font-weight: var(--cds-font-button-label-weight);
`;

export const CodeBlock = forwardRef<HTMLPreElement, React.HTMLAttributes<HTMLPreElement> & CodeBlockProps>(
    ({ className, children, styled = false, ...rest }, ref) => (
        <pre className={cn('bbui-code-block', { 'cds-t-code': styled }, className)} {...rest} ref={ref}>
            <code className={'bbui-monospace'}>{children}</code>
        </pre>
    )
);
CodeBlock.displayName = 'CodeBlock';

export const Code = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
    ({ className, children, ...rest }, ref) => (
        <code className={cn('bbui-code-inline', 'bbui-monospace', className)} {...rest} ref={ref}>
            {children}
        </code>
    )
);
Code.displayName = 'Code';

export const InlineCode = forwardRef<HTMLElement, React.HTMLAttributes<HTMLElement>>(
    ({ className, children, ...rest }, ref) => (
        <code className={cn('bbui-code-inline', 'cds-t-code', 'bbui-monospace', className)} {...rest} ref={ref}>
            {children}
        </code>
    )
);
InlineCode.displayName = 'InlineCode';

export const DataDisplay1 = createTypography('p', 'bbui-t-datadisplay1 cds-t-datadisplay1', 'DataDisplay1');
export const DataDisplay2 = createTypography('p', 'bbui-t-datadisplay2 cds-t-datadisplay2', 'DataDisplay2');

export const Numeric = ({
    value,
    className = '',
    locale = 'en-US',
    ...rest
}: {
    value: string | number;
    className?: string;
    locale?: string;
} & React.ComponentPropsWithoutRef<'span'>) => {
    const numberValue = typeof value === 'string' ? Number(value) : value;
    if (typeof numberValue !== 'number' || isNaN(numberValue)) {
        // eslint-disable-next-line no-console
        console.warn('Provided value to Number component is non-numeric');
        return <>{value}</>;
    }
    return (
        <span
            className={cn('bbui-numeric', className)}
            {...rest}
            style={{ fontVariantNumeric: 'tabular-nums', ...rest?.style }}
        >
            {new Intl.NumberFormat(locale, { maximumFractionDigits: 20 }).format(numberValue)}
        </span>
    );
};
Numeric.displayName = 'Numeric';

// ================ DISPLAY ================= //

export const D1 = createTypography('h1', 'bbui-t-d1 cds-t-d1', 'D1');
export const D1Bold = createTypography('h1', 'bbui-t-d1-bold cds-t-d1 cds-t-d1--b', 'D1Bold');
export const D2 = createTypography('h2', 'bbui-t-d2 cds-t-d2', 'D2');
export const D2Bold = createTypography('h2', 'bbui-t-d2-bold cds-t-d2 cds-t-d2--b', 'D2Bold');
export const D3 = createTypography('h3', 'bbui-t-d3 cds-t-d3', 'D3');
export const D3Bold = createTypography('h3', 'bbui-t-d3-bold cds-t-d3 cds-t-d3--b', 'D3Bold');
